import React, { useEffect, useState } from 'react';
import NavBar from './components/NavBar';
import Header from './components/Header';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {loadStripe} from "@stripe/stripe-js";

interface Order {
    id: string;
    amount: number;
    currency: string;
    status: string;
    name: string;
    email: string;
    phone: string;
    products: string;
    created: string;
}

const Orders: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCustomers, setTotalCustomers] = useState<number>(0);
    const [totalOrders, setTotalOrders] = useState<number>(0);

    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);
    const API_URL = process.env.REACT_APP_API_URL;

    const navigate = useNavigate();
    const [isNavBarMinimized, setIsNavBarMinimized] = useState(false);

    const toggleSidebar = () => {
        setIsNavBarMinimized(!isNavBarMinimized);
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [ordersRes, dashboardRes] = await Promise.all([
                    axios.get<Order[]>(`${API_URL}/payments`, { withCredentials: true }),
                    axios.get(`${API_URL}/order-data`, { withCredentials: true })
                ]);

                setOrders(ordersRes.data);
                setTotalCustomers(dashboardRes.data.totalCustomers);
                setTotalOrders(dashboardRes.data.totalOrders);
            } catch (err: any) {
                setError(`Failed to fetch data: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);


    return (
        <div className="h-screen">
            <NavBar isNavbarMinimized={isNavBarMinimized} />
            <div className={`flex flex-col md:ml-[290px] h-screen overflow-y-scroll ml-0 transition-all duration-300 ease-in-out`}>
                <Header toggleSidebar={toggleSidebar} isNavBarMinimized={isNavBarMinimized} />
                <div className="bg-neutral-100 flex flex-col flex-grow p-6">
                    <div className="bg-white border border-gray-200 w-full min-h-[25rem] rounded-2xl p-6 mb-6">
                        <h2 className="text-lg pb-4 text-gray-700 font-semibold">Recent Orders</h2>
                        <div className="relative">
                            {loading && (
                                    <div className="absolute position-center flex flex-col space-x-3 items-center justify-center">
                                        <h1 className="mb-4">Please wait! It takes a while to retrieve your order list.</h1>
                                        <div className="w-6 h-6 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin"></div>
                                    </div>
                            )}
                            <div className="overflow-hidden">
                                <div className="border border-gray-200 rounded-2xl overflow-x-auto overflow-y-auto">
                                    <div className="min-w-full h-[20rem]">
                                        <table className="min-w-full overflow-x-auto">
                                            <thead className="bg-gray-50 sticky top-0 w-full">
                                            <tr>
                                                <th className="font-medium text-xs p-4 text-gray-500 rounded-tl-2xl">Nr</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Amount (€)</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Status</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Customer Name</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Customer Email</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Phone</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left">Ordered</th>
                                                <th className="font-medium text-xs p-4 text-gray-500 text-left rounded-tr-2xl">Date Created</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {!loading && orders.length > 0 ? (
                                                orders.slice(0,5).map((order, index) => (
                                                    <tr key={order.id} className="whitespace-nowrap">
                                                        <td className="text-center text-gray-700 py-3 h-12">{index + 1}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.amount.toFixed(2)} €</td>
                                                        <td className="text-gray-700 py-3 h-12">
                                                            {order.status === 'succeeded' ? (
                                                                <div className="h-8 text-green-700 -ml-2 mr-5 bg-green-100 text-sm rounded-2xl flex text-center items-center justify-center">
                                                                    Paid
                                                                </div>
                                                            ) : (
                                                                <div className="h-8 text-red-700 mx-4 bg-red-100 text-sm rounded-2xl flex text-center items-center justify-center">
                                                                    {order.status}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="text-gray-700 py-3 h-12">{order.name}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.email}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.phone}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.products}</td>
                                                        <td className="text-gray-700 py-3 h-12"> {order.created.split(",")[0]}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                !loading && <tr><td colSpan={5} className="text-center text-gray-500 py-4">No orders found.</td></tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col space-y-6 lg:space-y-0 lg:flex-row lg:space-x-6">
                        <div className="w-full flex lg:flex-row flex-col lg:space-y-0 lg:space-x-6 space-y-6">
                            <div className="flex flex-col flex-grow">
                                <div className="bg-white border border-gray-200 w-full flex-grow rounded-2xl p-6">
                                    <div className="bg-gray-100 w-fit p-4 rounded-xl">
                                        <svg className="stroke-gray-800 h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <circle cx="9" cy="6" r="4" strokeWidth="1.5"></circle>
                                                <path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" strokeWidth="1.5" strokeLinecap="round"></path>
                                                <path d="M5.88915 20.5843C6.82627 20.8504 7.88256 21 9 21C12.866 21 16 19.2091 16 17C16 14.7909 12.866 13 9 13C5.13401 13 2 14.7909 2 17C2 17.3453 2.07657 17.6804 2.22053 18" strokeWidth="1.5" strokeLinecap="round"></path>
                                                <path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" strokeWidth="1.5" strokeLinecap="round"></path>
                                            </g>
                                        </svg>
                                    </div>
                                    <h1 className="text-gray-500 text-sm pt-5">Customers</h1>
                                    <h2 className="text-3xl font-semibold pt-4">{totalCustomers}</h2>
                                </div>
                            </div>
                            <div className="flex flex-col flex-grow">
                                <div className="bg-white border border-gray-200 w-full flex-grow rounded-2xl p-6">
                                    <div className="bg-gray-100 w-fit p-4 rounded-xl">
                                        <svg className="stroke-gray-800 h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <path d="M21.9844 10C21.9473 8.68893 21.8226 7.85305 21.4026 7.13974C20.8052 6.12523 19.7294 5.56066 17.5777 4.43152L15.5777 3.38197C13.8221 2.46066 12.9443 2 12 2C11.0557 2 10.1779 2.46066 8.42229 3.38197L6.42229 4.43152C4.27063 5.56066 3.19479 6.12523 2.5974 7.13974C2 8.15425 2 9.41667 2 11.9415V12.0585C2 14.5833 2 15.8458 2.5974 16.8603C3.19479 17.8748 4.27063 18.4393 6.42229 19.5685L8.42229 20.618C10.1779 21.5393 11.0557 22 12 22C12.9443 22 13.8221 21.5393 15.5777 20.618L17.5777 19.5685C19.7294 18.4393 20.8052 17.8748 21.4026 16.8603C21.8226 16.1469 21.9473 15.3111 21.9844 14" strokeWidth="1.5" strokeLinecap="round"></path>
                                                <path d="M21 7.5L17 9.5M12 12L3 7.5M12 12V21.5M12 12C12 12 14.7426 10.6287 16.5 9.75C16.6953 9.65237 17 9.5 17 9.5M17 9.5V13M17 9.5L7.5 4.5" strokeWidth="1.5" strokeLinecap="round"></path>
                                            </g>
                                        </svg>
                                    </div>
                                    <h1 className="text-gray-500 text-sm pt-5">Orders</h1>
                                    <h2 className="text-3xl font-semibold pt-4" >{totalOrders}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 flex flex-col flex-grow rounded-2xl mt-6">
                        <h2 className="p-6 border-b border-gray-200 text-gray-700 font-medium text-lg">All Orders</h2>
                        <div className="p-6 relative">
                            {loading && (
                                <div className="absolute position-center flex flex-col space-x-3 items-center justify-center">
                                    <h1 className="mb-4">Please wait! It takes a while to retrieve your order list.</h1>
                                    <div className="w-6 h-6 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin"></div>
                                </div>
                            )}
                            {error && <div className="text-red-500">{error}</div>}
                            <div className="max-w-full overflow-hidden">
                                <div className="border border-gray-200 rounded-2xl overflow-x-auto overflow-y-auto">
                                    <div className="min-w-[1024px] h-[500px]">
                                        <table className="w-full overflow-x-auto">
                                            <thead className="bg-gray-50 sticky top-0 w-full">
                                            <tr>
                                                <th className="font-medium text-xs p-4 text-gray-500 rounded-tl-2xl">Nr</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Amount (€)</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Status</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Customer Name</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Customer Email</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Phone</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left">Ordered</th>
                                                <th className="font-medium text-xs py-4 text-gray-500 text-left rounded-tr-2xl">Date Created</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {!loading && orders.length > 0 ? (
                                                orders.map((order, index) => (
                                                    <tr key={order.id} className="whitespace-nowrap">
                                                        <td className="text-center text-gray-700 py-3 h-12">{index + 1}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.amount.toFixed(2)} €</td>
                                                        <td className="text-gray-700 py-3 h-12">
                                                            {order.status === 'succeeded' ? (
                                                                <div className="h-8 text-green-700 -ml-2 mr-5 bg-green-100 text-sm rounded-2xl flex text-center items-center justify-center">
                                                                    Paid
                                                                </div>
                                                            ) : (
                                                                <div className="h-8 text-red-700 mx-4 bg-red-100 text-sm rounded-2xl flex text-center items-center justify-center">
                                                                    {order.status}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="text-gray-700 py-3 h-12">{order.name}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.email}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.phone}</td>
                                                        <td className="text-gray-700 py-3 h-12">{order.products}</td>
                                                        <td className="text-gray-700 py-3 h-12"> {order.created.split(",")[0]}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                !loading && <tr><td colSpan={10} className="text-center text-gray-500 py-4">No orders found.</td></tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Orders;
